import React, { useEffect, useState, useRef } from 'react';
import { ConfigProvider, App, Spin } from 'antd';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import enUS from 'antd/locale/en_US';
import svSE from 'antd/locale/sv_SE';
import { lightTheme } from '@/shared/theme/antdTheme';
import { AuthProvider } from '@/shared/lib/auth-context';
import { SettingsProvider } from '@/shared/lib/settings-context';
import { MSALInitProvider } from '@/shared/lib/msal-init-context';
import { msalConfig } from '@/shared/lib/msal';
import { logger } from '@/shared/utils/logger';

// Create MSAL instance as singleton outside of React component lifecycle
// This ensures it survives React StrictMode's double-mounting and HMR
let msalInstance: PublicClientApplication | null = null;
let msalInitPromise: Promise<void> | null = null;

const getMsalInstance = (): PublicClientApplication => {
  if (!msalInstance) {
    logger.debug('[MSAL] Creating new PublicClientApplication instance');
    msalInstance = new PublicClientApplication(msalConfig);
  }
  return msalInstance;
};

const initializeMsal = async (): Promise<PublicClientApplication> => {
  const instance = getMsalInstance();

  // If already initialized or initialization in progress, return existing promise
  if (msalInitPromise) {
    logger.debug('[MSAL] Initialization already in progress, waiting...');
    await msalInitPromise;
    return instance;
  }

  // Start initialization
  msalInitPromise = (async () => {
    logger.debug('[MSAL] Starting MSAL initialization');

    await instance.initialize();
    logger.debug('[MSAL] MSAL instance initialized');

    // Load accounts from cache
    const accounts = instance.getAllAccounts();
    logger.debug('[MSAL] Loaded accounts from cache', { count: accounts.length });

    // Set first account as active if we have one
    if (accounts.length > 0) {
      if (!instance.getActiveAccount()) {
        logger.debug('[MSAL] Setting first account as active', { username: accounts[0].username });
        instance.setActiveAccount(accounts[0]);
      }
    }

    // Check if we're returning from a Microsoft login redirect
    const currentUrl = window.location.href;
    const hasRedirectParams =
      currentUrl.includes('#') ||
      currentUrl.includes('code=') ||
      currentUrl.includes('state=') ||
      currentUrl.includes('error=');

    if (hasRedirectParams) {
      logger.debug('[MSAL] Detected redirect parameters, processing...');
      const result = await instance.handleRedirectPromise();

      if (result?.account) {
        logger.debug('[MSAL] Setting active account from redirect', { username: result.account.username });
        instance.setActiveAccount(result.account);

        // Clear URL params to prevent MsalProvider from processing again
        window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
      }
    }

    logger.debug('[MSAL] Initialization complete', {
      accountCount: instance.getAllAccounts().length,
      activeAccount: instance.getActiveAccount()?.username,
    });
  })();

  await msalInitPromise;
  return instance;
};

// Create QueryClient instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

interface AppProvidersProps {
  children: React.ReactNode;
}

// Component that provides ConfigProvider with proper locale
const LocaleAwareConfigProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { i18n } = useTranslation();
  
  const getAntdLocale = () => {
    switch (i18n.language) {
      case 'sv':
        return svSE;
      default:
        return enUS;
    }
  };

  return (
    <ConfigProvider theme={lightTheme} locale={getAntdLocale()}>
      <App>
        {children}
      </App>
    </ConfigProvider>
  );
};

export const AppProviders: React.FC<AppProvidersProps> = ({ children }) => {
  const [msalReady, setMsalReady] = useState(false);
  const [msalInitialized, setMsalInitialized] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    // Track if component is still mounted (for cleanup)
    mountedRef.current = true;

    // Initialize MSAL
    initializeMsal()
      .then(() => {
        // Only update state if component is still mounted
        if (mountedRef.current) {
          setMsalInitialized(true);
          setMsalReady(true);
        }
      })
      .catch((error) => {
        logger.error('[MSAL] Initialization error', error);
        // Proceed anyway to show error UI rather than infinite loading
        if (mountedRef.current) {
          setMsalInitialized(true);
          setMsalReady(true);
        }
      });

    // Cleanup function for StrictMode
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Show loading spinner while MSAL initializes
  if (!msalReady) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        gap: '16px'
      }}>
        <Spin size="large" />
        <div style={{ color: '#666', fontSize: '14px' }}>Initializing...</div>
      </div>
    );
  }

  return (
    <MsalProvider instance={getMsalInstance()}>
      <MSALInitProvider msalInitialized={msalInitialized}>
        <AuthProvider>
          <QueryClientProvider client={queryClient}>
            <SettingsProvider>
              <LocaleAwareConfigProvider>
                {children}
              </LocaleAwareConfigProvider>
            </SettingsProvider>
          </QueryClientProvider>
        </AuthProvider>
      </MSALInitProvider>
    </MsalProvider>
  );
};